import React, { useMemo } from "react";

function pad2(n) {
  return String(n).padStart(2, "0");
}

function toIsoDate(date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function monthLabel(year, monthIndex) {
  try {
    const label = new Intl.DateTimeFormat("pt-BR", {
      month: "long",
      year: "numeric",
    }).format(new Date(year, monthIndex, 1));
    return label.charAt(0).toUpperCase() + label.slice(1);
  } catch {
    return `${monthIndex + 1}/${year}`;
  }
}

function buildMonthCells(year, monthIndex) {
  const first = new Date(year, monthIndex, 1);
  const last = new Date(year, monthIndex + 1, 0);
  const firstDay = first.getDay();
  const daysInMonth = last.getDate();
  const prevLast = new Date(year, monthIndex, 0).getDate();
  const cells = [];

  for (let i = 0; i < firstDay; i += 1) {
    const day = prevLast - (firstDay - 1 - i);
    cells.push({
      isoDate: "",
      day,
      inMonth: false,
    });
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    const date = new Date(year, monthIndex, day);
    cells.push({
      isoDate: toIsoDate(date),
      day,
      inMonth: true,
    });
  }

  while (cells.length < 42) {
    const nextDay = cells.length - (firstDay + daysInMonth) + 1;
    cells.push({
      isoDate: "",
      day: nextDay,
      inMonth: false,
    });
  }

  return cells;
}

export default function YearCalendarBoard({
  year,
  onYearChange,
  dateMeta = {},
  onDayClick,
  title,
  subtitle,
}) {
  const months = useMemo(
    () =>
      Array.from({ length: 12 }).map((_, monthIndex) => ({
        monthIndex,
        label: monthLabel(year, monthIndex),
        cells: buildMonthCells(year, monthIndex),
      })),
    [year]
  );

  const dow = ["D", "S", "T", "Q", "Q", "S", "S"];

  return (
    <section className="swb-panel">
      <div className="swb-panel__head">
        <div>
          {title ? <h3>{title}</h3> : null}
          {subtitle ? <div className="swb-subtle">{subtitle}</div> : null}
        </div>
        <div className="swb-inline-actions">
          <button type="button" className="swb-btn" onClick={() => onYearChange(year - 1)}>
            {year - 1}
          </button>
          <button type="button" className="swb-btn swb-btn--primary" onClick={() => onYearChange(year)}>
            {year}
          </button>
          <button type="button" className="swb-btn" onClick={() => onYearChange(year + 1)}>
            {year + 1}
          </button>
        </div>
      </div>

      <div className="swb-year-grid">
        {months.map((month) => (
          <div key={month.monthIndex} className="swb-month-card">
            <div className="swb-month-card__title">{month.label}</div>
            <div className="swb-month-card__dow">
              {dow.map((item, idx) => (
                <div key={`${month.monthIndex}_dow_${idx}`}>{item}</div>
              ))}
            </div>
            <div className="swb-month-card__cells">
              {month.cells.map((cell, idx) => {
                if (!cell.inMonth) {
                  return (
                    <div key={`${month.monthIndex}_${idx}`} className="swb-day swb-day--ghost">
                      {cell.day}
                    </div>
                  );
                }

                const meta = dateMeta[cell.isoDate] || null;
                const tone = meta?.tone || "default";
                const filled = !!meta;
                const label = meta?.label || "";

                return (
                  <button
                    key={`${month.monthIndex}_${cell.isoDate}`}
                    type="button"
                    className={`swb-day swb-day--${tone} ${filled ? "is-filled" : ""}`}
                    title={label ? `${cell.isoDate} · ${label}` : cell.isoDate}
                    onClick={() => onDayClick?.(cell.isoDate, meta)}
                  >
                    <span>{cell.day}</span>
                    {meta?.badge ? <small>{meta.badge}</small> : null}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
